import * as React from 'react';
import { AdvancedPlansFeature, AdvancedPlansProductData } from 'types';
import s from './PlansGrid.scss';
import groupStyle from './Group.scss';
import Check from 'wix-ui-icons-common/Check';
import HelpCircle from 'wix-ui-icons-common/HelpCircle';
import MinusSmall from 'wix-ui-icons-common/MinusSmall';
import classNames from 'classnames';
import { Text } from 'wix-style-react';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { withInjectedRootStore, RootStore } from 'providers';
import { Trans, useTranslation } from 'react-i18next';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';
import { useDelayedTooltipBiEvent } from '../../../../hooks/useDelayedTooltipBiEvent';
import { Tooltip } from '../../../../components/Tooltip';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { getAdVoucherTransParams } from 'utils';
import { TruncatedTextWrapper } from '../../../../components/TruncatedTextTooltip';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { getCountryCodeTld } from '../../../../utils';
import { useBreakpoint } from 'providers/BreakpointProvider';
import { MediaDevices } from 'enums';

interface FeatureRowProps extends InjectedExperimentsProps {
  products: AdvancedPlansProductData[];
  feature: AdvancedPlansFeature;
  rootStore: RootStore;
  className?: string;
  cellClassName?: string;
  style?: React.CSSProperties;
}

export const FeatureRowComp = React.memo<FeatureRowProps>(
  ({ products, feature, rootStore, className, style, cellClassName }) => {
    const { appStore } = rootStore;
    const {
      currentCycle,
      appContext: { biLogger, requestParamsMap, country },
      isJapanesePackagePicker,
      isInSale,
    } = appStore;
    const { t } = useTranslation();

    const { currentBreakpoint } = useBreakpoint();

    const tooltipsAsASubtitle =
      isJapanesePackagePicker && currentBreakpoint === MediaDevices.Desktop;

    const {
      amountToSpend,
      amount,
      currencyCode,
    } = appStore.googleVoucherAmounts;
    const [currencyToString] = usePremiumCurrencyFormatter({
      currencyCode,
    });
    const voucherTransParams = getAdVoucherTransParams(
      currencyToString,
      amount,
      amountToSpend,
    );
    const domainFeatureTransParams = {
      domain: requestParamsMap.domainName,
      ccTLD: `${getCountryCodeTld(country)}, .co.uk`, // for tooltip
    };

    const transParams = { ...voucherTransParams, ...domainFeatureTransParams };

    const featureValue = React.useCallback((productId) => {
      if (!feature.value) {
        return <Check />;
      }
      const key = feature.value.get(productId);
      return key && t(key);
    }, []);
    const onTooltipViewed = () =>
      biLogger.logViewProductFeatureTooltip({
        cycle: PaymentCycle_BIMap[currentCycle],
        fromStep: 'select_plan',
        productId: 0,
        featureKey: feature.translationKey,
      });
    const {
      logShowTooltipDelayedEvent,
      interuptTooltipDelayedEvent,
    } = useDelayedTooltipBiEvent(onTooltipViewed);
    const _products: [string, boolean][] = React.useMemo(
      () =>
        products.map((p) => [
          p.id.toString(),
          new AdvancedPlansProductModel(p, appStore).isSelected(),
        ]),
      [products],
    );
    const featureExistsInProducts = _products
      .map(([id]) => feature.includedInProducts.has(id))
      .some((v) => v);
    if (!featureExistsInProducts) {
      return null;
    }
    const translationTags = {
      b: <b />,
      ul: <ul />,
      li: <li />,
      div: <div data-hook='voucher-disclaimer' className={s.adVouchers} />,
      ellipsis: (
        <TruncatedTextWrapper
          dataHook='truncated-domain-name'
          text={requestParamsMap.domainName}
          maxWidth={136}
          maxTextLength={17}
          componentProps={{
            weight: 'bold',
            size: 'small',
          }}
        />
      ),
    };

    return (
      <div className={classNames(s.gridRow, className)} style={style}>
        <div
          className={classNames(s.cell, s.featureName, cellClassName)}
          data-testid='feature-name'
          data-is-ad-voucher={feature.isGoogleVoucher}
          key={feature.translationKey}
        >
          <div className={classNames(s.featureName, groupStyle.featureName)}>
            <Text size='small'>
              <Trans
                i18nKey={feature.translationKey}
                components={translationTags}
                values={transParams}
              />
            </Text>

            {!tooltipsAsASubtitle && feature.benefit && (
              <Text size='tiny' secondary dataHook='feature-benefit'>
                {t(feature.benefit)}
              </Text>
            )}

            {tooltipsAsASubtitle && (
              <div>
                <Text
                  size='tiny'
                  data-testid='feature-description'
                  className={s.descriptionJapan}
                >
                  <Trans
                    i18nKey={feature.tooltipTranslationKey}
                    components={translationTags}
                    values={transParams}
                  />
                </Text>
                {feature.benefit && (
                  <Text size='tiny' secondary dataHook='feature-benefit'>
                    {' ' + t(feature.benefit)}
                  </Text>
                )}
              </div>
            )}
          </div>
          {!tooltipsAsASubtitle && (
            <Tooltip
              dataHook='feature-tooltip'
              maxWidth={300}
              placement='right'
              onOpen={logShowTooltipDelayedEvent}
              onClose={interuptTooltipDelayedEvent}
              content={
                <Trans
                  i18nKey={feature.tooltipTranslationKey}
                  components={translationTags}
                  values={transParams}
                >
                  <Text size='small'>{feature.tooltipTranslationKey}</Text>
                </Trans>
              }
            >
              {isJapanesePackagePicker ? (
                <HelpCircle />
              ) : (
                <i className={s.noteIcon} />
              )}
            </Tooltip>
          )}
        </div>
        {_products.map(([id, isPreselected], idx) =>
          feature.includedInProducts.has(id.toString()) ? (
            <div
              key={`v-icon-${idx}`}
              data-testid={`v-icon-${idx}`}
              className={classNames(s.cell, cellClassName, {
                [s.preselected]: isPreselected,
                [s.japanCell]: isJapanesePackagePicker,
                [s.japanIconCellSale]: isJapanesePackagePicker && isInSale,
              })}
            >
              <Text size='small'>{featureValue(id)}</Text>
            </div>
          ) : (
            <div
              key={`x-icon-${idx}`}
              data-testid={`x-icon-${idx}`}
              className={classNames(s.cell, cellClassName, {
                [s.preselected]: isPreselected,
              })}
            >
              <MinusSmall className={s.minus} />
            </div>
          ),
        )}
      </div>
    );
  },
);

export const FeatureRow = withExperiments(
  withInjectedRootStore(FeatureRowComp),
);
